/**
 * ForceStatusDialog — Wave 1n
 *
 * Universal admin recovery tool. Lets a superadmin take any `org_id` and
 * forcibly set its subscription status (demo / active / trial_expired /
 * suspended) regardless of current state. Synchronises BOTH the
 * `organizations` collection (Demos Kanban) AND the `tenants` collection
 * (Tenants list).
 *
 * Used for:
 *   - Recovering demos that "disappeared" (e.g. was wrongly converted)
 *   - Downgrading a paying tenant back to demo for testing
 *   - Manually granting a paid plan without Stripe
 */
import React, { useState } from 'react';
import { Loader2, Search, Wrench, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const STATUS_OPTIONS = [
  { value: 'demo', label: '🧪 Démo (en essai)', help: 'Remet l\'org dans le Kanban Démos avec un essai réinitialisé.' },
  { value: 'active', label: '✅ Active (tenant payant)', help: 'Promeut comme client payant. Demande un plan.' },
  { value: 'trial_expired', label: '⏰ Essai expiré', help: 'Marque l\'essai comme terminé sans suspension.' },
  { value: 'suspended', label: '🛑 Suspendu', help: 'Bloque toutes les sessions de l\'org.' },
];

export default function ForceStatusDialog({ open, onOpenChange, onSuccess }) {
  const [orgId, setOrgId] = useState('');
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState('active');
  const [plan, setPlan] = useState('pro');
  const [trialDays, setTrialDays] = useState(15);
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);

  const reset = () => {
    setOrgId(''); setLookup(null); setReason(''); setTarget('active');
    setPlan('pro'); setTrialDays(15);
  };

  const handleLookup = async () => {
    if (!orgId.trim()) return;
    setLoading(true); setLookup(null);
    try {
      const { data } = await api.get(`/superadmin/orgs/${orgId.trim()}/lookup`);
      setLookup(data);
    } catch (err) {
      toast({
        title: 'Org introuvable',
        description: err?.response?.data?.detail || `Aucune trace de ${orgId}`,
      });
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!lookup) return;
    setWorking(true);
    try {
      const payload = {
        status: target,
        plan_code: target === 'active' ? plan : null,
        trial_days: target === 'demo' ? trialDays : null,
        reason: reason.trim() || `Force override via Superadmin UI`,
      };
      const { data } = await api.post(`/superadmin/orgs/${lookup.org_id}/force-status`, payload);
      toast({
        title: '✅ Statut forcé avec succès',
        description: `${data.org.name || lookup.org_id} : ${data.previous_status || '—'} → ${data.new_status}`,
      });
      onSuccess?.();
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setWorking(false); }
  };

  if (!open) return null;

  const currentStatus = lookup?.organization?.subscription_status;
  const currentStage = lookup?.organization?.demo_kanban_stage;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="force-status-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-[640px] max-w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#4F46E5]" />
            <h2 className="text-[15px] font-bold text-[#1A1B2E]">Récupérer / Forcer le statut d&apos;un tenant</h2>
          </div>
          <button onClick={() => { reset(); onOpenChange(false); }} className="text-[#6B6C85] hover:text-[#1A1B2E]" data-testid="force-status-close">✕</button>
        </div>

        {/* Step 1 — Lookup */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-[#1A1B2E] mb-1.5">
              1. ID de l&apos;organisation
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                placeholder="org_79be9d435e90"
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                data-testid="force-status-org-id"
                className="flex-1 h-10 px-3 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE] text-[13px] font-mono"
              />
              <button
                onClick={handleLookup}
                disabled={!orgId.trim() || loading}
                data-testid="force-status-lookup"
                className="h-10 px-4 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                Chercher
              </button>
            </div>
          </div>

          {/* Step 2 — Lookup result */}
          {lookup && (
            <div className="rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] p-3 space-y-1.5 text-[12px]" data-testid="force-status-lookup-result">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#15803D]" />
                <span className="font-semibold text-[#1A1B2E]">{lookup.organization?.name || '(sans nom)'}</span>
                <span className="text-[10px] text-[#6B6C85] font-mono">{lookup.org_id}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] text-[#6B6C85]">
                <div>Statut actuel : <strong className="text-[#1A1B2E]">{currentStatus || '—'}</strong></div>
                <div>Étape Kanban : <strong className="text-[#1A1B2E]">{currentStage || '—'}</strong></div>
                <div>Dans Tenants : <strong className="text-[#1A1B2E]">{lookup.tenant ? `Oui (${lookup.tenant.status})` : 'Non'}</strong></div>
                <div>Utilisateurs : <strong className="text-[#1A1B2E]">{lookup.users_count}</strong></div>
                <div>Owner email : <strong className="text-[#1A1B2E]">{(lookup.organization?.demo_contact || {}).owner_email || lookup.tenant?.contact_email || '—'}</strong></div>
                <div>Plan tenant : <strong className="text-[#1A1B2E]">{lookup.tenant?.plan_code || '—'}</strong></div>
              </div>
            </div>
          )}

          {/* Step 3 — Target status */}
          {lookup && (
            <>
              <div>
                <label className="block text-[12px] font-semibold text-[#1A1B2E] mb-1.5">
                  2. Statut cible
                </label>
                <div className="space-y-1.5">
                  {STATUS_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${target === opt.value ? 'border-[#4F46E5] bg-[#EEF2FF]' : 'border-[#EFF0F6] hover:bg-[#FAFAFE]'}`}
                    >
                      <input
                        type="radio"
                        name="target"
                        value={opt.value}
                        checked={target === opt.value}
                        onChange={(e) => setTarget(e.target.value)}
                        data-testid={`force-status-target-${opt.value}`}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-[12.5px] font-semibold text-[#1A1B2E]">{opt.label}</div>
                        <div className="text-[11px] text-[#6B6C85]">{opt.help}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Conditional inputs */}
              {target === 'active' && (
                <div>
                  <label className="block text-[12px] font-semibold text-[#1A1B2E] mb-1.5">
                    3. Plan à attribuer
                  </label>
                  <select
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    data-testid="force-status-plan"
                    className="w-full h-10 px-3 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE] text-[13px]"
                  >
                    <option value="starter">Starter (49€/mois)</option>
                    <option value="pro">Pro (99€/mois)</option>
                    <option value="enterprise">Enterprise (299€/mois)</option>
                  </select>
                </div>
              )}

              {target === 'demo' && (
                <div>
                  <label className="block text-[12px] font-semibold text-[#1A1B2E] mb-1.5">
                    3. Durée d&apos;essai (jours)
                  </label>
                  <input
                    type="number"
                    min={1} max={365}
                    value={trialDays}
                    onChange={(e) => setTrialDays(parseInt(e.target.value, 10) || 15)}
                    data-testid="force-status-trial-days"
                    className="w-full h-10 px-3 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE] text-[13px]"
                  />
                </div>
              )}

              <div>
                <label className="block text-[12px] font-semibold text-[#1A1B2E] mb-1.5">
                  Raison (audit log)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: récupération démo disparue / paiement manuel reçu / …"
                  data-testid="force-status-reason"
                  className="w-full h-10 px-3 rounded-lg border border-[#EFF0F6] bg-[#FAFAFE] text-[13px]"
                />
              </div>

              {/* Warning banner for destructive transitions */}
              {(target === 'demo' && currentStatus === 'active') && (
                <div className="rounded-lg bg-[#FEF3C7] border border-[#FCD34D] p-3 flex items-start gap-2 text-[11.5px] text-[#92400E]" data-testid="force-status-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <strong>Attention</strong> : vous allez rétrograder un tenant payant en démo. La row dans <code>tenants</code> sera supprimée (la facturation Stripe ne sera PAS annulée — pensez à le faire dans Stripe Dashboard si besoin).
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#EFF0F6] flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={() => { reset(); onOpenChange(false); }}
            className="h-9 px-4 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-[#FAFAFE]"
            data-testid="force-status-cancel"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!lookup || working}
            data-testid="force-status-submit"
            className="h-9 px-5 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            {working && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Forcer le statut
          </button>
        </div>
      </div>
    </div>
  );
}
